import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import { createCampaign, fetchCampaigns } from '../../redux/features/magicLampSlice';

/**
 * Magic Lamp — campaign list
 *
 * Each campaign is one wheel, with its own segments and chances.
 */
const STATUS_COLOR = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  ended: 'default',
};

export default function CampaignList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const { campaigns, loadingCampaigns, saving } = useSelector((state) => state.magicLamp);

  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    dispatch(fetchCampaigns())
      .unwrap()
      .catch((error) => enqueueSnackbar(error.message, { variant: 'error' }));
  }, [dispatch, enqueueSnackbar]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      // unwrap() turns a rejected thunk into a throw, so the happy path below
      // only runs when the campaign really was created.
      const created = await dispatch(createCampaign({ name: name.trim() })).unwrap();
      enqueueSnackbar('Campaign created', { variant: 'success' });
      setCreating(false);
      setName('');
      navigate(`/magic-lamp/spin/campaigns/${created.data.id}`);
    } catch (error) {
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  return (
    <GlobalWrapper
      title="Lucky Spin"
      actions={
        <Stack direction="row" gap={1}>
          <Button onClick={() => navigate('/magic-lamp/spin/analytics')}>Analytics</Button>
          <Button onClick={() => navigate('/magic-lamp/spin/log')}>Spin log</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
            New campaign
          </Button>
        </Stack>
      }
    >
      <Typography variant="body2" color="text.secondary" mb={3}>
        Each campaign is one wheel, with its own prizes and chances.
      </Typography>

      {loadingCampaigns && (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      )}

      {!loadingCampaigns && campaigns.length === 0 && (
        <Card variant="outlined">
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography fontWeight={600}>No campaigns yet</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Create one, set the prizes and their chances, then make it live.
            </Typography>
          </CardContent>
        </Card>
      )}

      <Grid container spacing={2}>
        {campaigns.map((campaign) => (
          <Grid item xs={12} sm={6} md={4} key={campaign.id}>
            <Card variant="outlined">
              <CardActionArea onClick={() => navigate(`/magic-lamp/spin/campaigns/${campaign.id}`)}>
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    gap={1}
                  >
                    <Typography fontWeight={700}>{campaign.name}</Typography>
                    <Chip
                      size="small"
                      label={campaign.status}
                      color={STATUS_COLOR[campaign.status] || 'default'}
                    />
                  </Stack>

                  <Grid container spacing={1} mt={1.5} textAlign="center">
                    {[
                      // Two numbers, because "8 segments" alone hides that half
                      // of them may be switched off.
                      {
                        label: 'Segments',
                        value: `${campaign.activeSegmentCount}/${campaign.segmentCount}`,
                      },
                      { label: 'Spins', value: campaign.totalSpins },
                      { label: 'Wins', value: campaign.totalWins },
                    ].map((stat) => (
                      <Grid item xs={4} key={stat.label}>
                        <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, py: 1 }}>
                          <Typography fontWeight={800}>{stat.value}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {stat.label}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={800}>New campaign</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Name"
            placeholder="Onam 2026"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleCreate()}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate} disabled={!name.trim() || saving}>
            {saving ? 'Creating…' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

    </GlobalWrapper>
  );
}